import { useRouteLoaderData } from 'react-router';
import { tournamentRouteId } from './layout';
import { clubApi } from '@repo/api-client';
import { useRevalidator } from 'react-router';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/components/dialog';
import { Calendar, Check, Clock, Eye, Hourglass, TrashIcon, UserX } from 'lucide-react';
import { NotOkStatusIndicator, OkStatusIndicator } from '@repo/ui/components/status-indicator';
import { useState } from 'react';
import { API_URL } from '@/data/constants';
import { Share } from '@/components/ui/share';
import { DateFormat, formatDate } from '@/data/format/format-date';
import { DataTable } from '@repo/ui/components/data-table';
import { DataTableRowActions } from '@repo/ui/components/data-table-row-actions';

interface TimeInterest {
  id: string;
  confirmed: boolean;
  courtReservation: {
    id: string;
    start: string;
    end: string;
    court: {
      id: string;
      name: string;
    };
  };
}

interface RegistrationWithInterests extends Registration {
  timeInterests?: TimeInterest[];
}

type ScheduleStatus = 'waiting_list' | 'pending_assignment' | 'assigned';

function getScheduleStatus(registration: RegistrationWithInterests): ScheduleStatus {
  if (!registration.timeInterests || registration.timeInterests.length === 0) {
    return 'waiting_list';
  }
  const hasConfirmed = registration.timeInterests.some((i) => i.confirmed);
  return hasConfirmed ? 'assigned' : 'pending_assignment';
}

function ScheduleStatusIcon({ status }: { status: ScheduleStatus }) {
  switch (status) {
    case 'waiting_list':
      return (
        <span title="Lista de espera - No seleccionó horarios">
          <UserX className="w-4 h-4 text-red-500" />
        </span>
      );
    case 'pending_assignment':
      return (
        <span title="Esperando asignación - Seleccionó horarios, esperando que se forme un grupo">
          <Hourglass className="w-4 h-4 text-orange-500" />
        </span>
      );
    case 'assigned':
      return (
        <span title="Asignado - Ya tiene horario de juego confirmado">
          <Check className="w-4 h-4 text-green-600" />
        </span>
      );
  }
}

function OpenInscriptions() {
  const { tournament } = useRouteLoaderData(tournamentRouteId);
  const revalidator = useRevalidator();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] =
    useState<RegistrationWithInterests | null>(null);

  const removeRegistration = async (id: string) => {
    await clubApi.removeRegistration(tournament.club.id, tournament.id, id);
    revalidator.revalidate();
  };

  const openDetails = (registration: RegistrationWithInterests) => {
    setSelectedRegistration(registration);
    setDetailsOpen(true);
  };

  const columns = [
    {
      header: 'Categoría',
      accessorFn: (row: RegistrationWithInterests) => row.category.name,
      hidden: true,
    },
    {
      header: 'Categoría',
      accessorKey: 'category',
      cell: ({ row }: { row: { original: RegistrationWithInterests } }) => {
        return row.original.category.name;
      },
    },
    {
      header: 'Jugador 1',
      accessorKey: 'playerOne',
    },
    {
      header: 'Jugador 2',
      accessorKey: 'playerTwo',
    },
    {
      header: 'Confirmado',
      accessorKey: 'confirmed',
      cell: ({ row }: { row: { original: RegistrationWithInterests } }) => {
        return row.original.confirmed ? <OkStatusIndicator /> : <NotOkStatusIndicator />;
      },
    },
    {
      header: 'Horario',
      accessorKey: 'timeInterests',
      cell: ({ row }: { row: { original: RegistrationWithInterests } }) => {
        return <ScheduleStatusIcon status={getScheduleStatus(row.original)} />;
      },
    },
    {
      header: 'Celular',
      accessorKey: 'phone',
      className: 'hidden md:table-cell',
    },
    {
      header: 'Acciones',
      accessorKey: 'actions',
      cell: ({ row }: { row: { original: RegistrationWithInterests } }) => {
        return (
          <DataTableRowActions
            actions={[
              {
                label: 'Ver detalles',
                onClick: () => openDetails(row.original),
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: 'Eliminar',
                onClick: () => removeRegistration(row.original.id),
                icon: <TrashIcon className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        total={tournament.registrations.length}
        columns={columns}
        data={tournament.registrations}
        mainActions={() => (
          <Share
            data={{
              title: 'Inscribite al torneo',
              url: `${API_URL}/og/torneo/${tournament.id}`,
            }}
            title="Compartir pagina de inscripciones"
          >
            Compartir pagina de inscripciones
          </Share>
        )}
      />

      {/* Registration Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalles de Inscripción</DialogTitle>
          </DialogHeader>
          {selectedRegistration && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Jugadores</span>
                  <span className="text-sm font-medium">
                    {selectedRegistration.playerOne} - {selectedRegistration.playerTwo}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Teléfono</span>
                  <span className="text-sm font-medium">{selectedRegistration.phone || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Confirmado</span>
                  <span className="text-sm font-medium">
                    {selectedRegistration.confirmed ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <Check className="w-4 h-4" /> Sí
                      </span>
                    ) : (
                      <span className="text-orange-600">No</span>
                    )}
                  </span>
                </div>
              </div>

              {selectedRegistration.timeInterests &&
                selectedRegistration.timeInterests.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium border-t pt-3">
                      <Clock className="w-4 h-4" />
                      Horarios Seleccionados
                    </div>
                    <div className="space-y-2">
                      {selectedRegistration.timeInterests.map((interest) => {
                        const startDate = new Date(interest.courtReservation.start);
                        const endDate = new Date(interest.courtReservation.end);
                        const dateStr = formatDate(startDate, DateFormat.DD_MM_YYYY);
                        const startTime = formatDate(startDate, DateFormat.HH_MM);
                        const endTime = formatDate(endDate, DateFormat.HH_MM);

                        return (
                          <div
                            key={interest.id}
                            className={`flex items-center justify-between p-2 rounded-lg border ${
                              interest.confirmed
                                ? 'border-green-300 bg-green-50'
                                : 'border-neutral-200 bg-neutral-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {interest.confirmed && <Check className="w-4 h-4 text-green-600" />}
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3 h-3 text-muted-foreground" />
                                <span className="text-sm">{dateStr}</span>
                                <span className="text-sm text-muted-foreground">
                                  {startTime} - {endTime}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {interest.courtReservation.court.name}
                              </span>
                              {interest.confirmed && (
                                <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">
                                  Asignado
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {(!selectedRegistration.timeInterests ||
                selectedRegistration.timeInterests.length === 0) && (
                <div className="text-sm text-muted-foreground text-center py-4 border-t">
                  No hay horarios seleccionados
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export const route = {
  index: true,
  path: 'open-inscriptions',
  element: <OpenInscriptions />,
};
